/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Keyboard Shortcuts Hook
 *
 * Provides keyboard shortcut handling for the application
 */

import { useEffect, useCallback, useRef } from "react";

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  description: string;
  handler: () => void;
  preventDefault?: boolean;
}

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
}

/**
 * Hook to register keyboard shortcuts
 *
 * @param options - Configuration options
 * @returns Object with utility functions
 *
 * @example
 * ```tsx
 * useKeyboardShortcuts({
 *   shortcuts: [
 *     { key: 'n', description: 'New class', handler: handleCreate },
 *     { key: 'ArrowLeft', description: 'Previous week', handler: handlePreviousWeek },
 *   ],
 *   enabled: true,
 * });
 * ```
 */
export const useKeyboardShortcuts = ({
  shortcuts,
  enabled = true,
}: UseKeyboardShortcutsOptions) => {
  const shortcutsRef = useRef(shortcuts);

  // Update ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when user is typing in an input/textarea
      const target = event.target as HTMLElement;
      const isInputField =
        target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;

      // Find matching shortcut
      const matchingShortcut = shortcutsRef.current.find((shortcut) => {
        // For input fields, only allow Escape key
        if (isInputField && shortcut.key !== "Escape") {
          return false;
        }

        // Check if key matches (case-insensitive for letters)
        const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();

        if (!keyMatches) return false;

        // Check modifier keys
        const ctrlMatches = shortcut.ctrlKey
          ? event.ctrlKey || event.metaKey
          : !event.ctrlKey && !event.metaKey;
        const shiftMatches = shortcut.shiftKey ? event.shiftKey : !event.shiftKey;
        const altMatches = shortcut.altKey ? event.altKey : !event.altKey;

        return keyMatches && ctrlMatches && shiftMatches && altMatches;
      });

      if (matchingShortcut) {
        if (matchingShortcut.preventDefault !== false) {
          event.preventDefault();
        }
        matchingShortcut.handler();
      }
    },
    [enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enabled, handleKeyDown]);

  return {
    shortcuts: shortcutsRef.current,
  };
};

/**
 * Get a formatted display string for a keyboard shortcut
 */
export const getShortcutDisplay = (shortcut: KeyboardShortcut): string => {
  const parts: string[] = [];

  if (shortcut.ctrlKey || shortcut.metaKey) {
    // Show Cmd on Mac, Ctrl on other platforms
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    parts.push(isMac ? "⌘" : "Ctrl");
  }

  if (shortcut.shiftKey) {
    parts.push("⇧");
  }

  if (shortcut.altKey) {
    parts.push(navigator.platform.toUpperCase().indexOf("MAC") >= 0 ? "⌥" : "Alt");
  }

  // Format key display
  let keyDisplay = shortcut.key;
  if (keyDisplay === "ArrowLeft") keyDisplay = "←";
  else if (keyDisplay === "ArrowRight") keyDisplay = "→";
  else if (keyDisplay === "ArrowUp") keyDisplay = "↑";
  else if (keyDisplay === "ArrowDown") keyDisplay = "↓";
  else if (keyDisplay === "Escape") keyDisplay = "Esc";
  else keyDisplay = keyDisplay.toUpperCase();

  parts.push(keyDisplay);

  return parts.join(" + ");
};
